import { Booking } from "PFTypes";
import React from "react";

import { BookingDetailSidePanel } from "../../parts/overview/detail_view/booking_detail/booking_detail_side_panel";
import { EngagementSidePanelWrapper } from "./detail_view/engagement_detail_side_panel";
import { OverbookingsDetailSidePanel } from "./detail_view/overbookings_detail/overbookings_detail_side_panel";
import { ProfileDetailsSidePanel } from "./detail_view/profile_detail/profile_detail_side_panel";
import { RoleDetailSidePanelWrapper } from "./detail_view/role_detail_side_panel";
import { DetailsPanelData, useDetailsPanelContext } from "./details_panel_context_provider";
import { usePrerequisiteChecker } from "./use_prerequisite_checker";

const SidePanel = {
  booking: BookingDetailSidePanel,
  engagement: EngagementSidePanelWrapper,
  profile: ProfileDetailsSidePanel,
  role: RoleDetailSidePanelWrapper,
  overbookings: OverbookingsDetailSidePanel
};

export type DetailsSidePanelProps = {
  show: boolean;
  isOnTop: boolean;
  onClose: () => void;
};

export interface DetailsPanelComponentProps {
  data: DetailsPanelData;
  onDeleted: (booking: Booking) => Promise<void>;
  onEdited: () => void;
}

export const DetailsPanel = ({ data, onDeleted, onEdited }: DetailsPanelComponentProps): JSX.Element => {
  const {
    detailsDataStack,
    closeDetailsPanel,
    displayOrder: { checkTopValue }
  } = useDetailsPanelContext();
  const checkIfSidePanelCanBeDisplayed = usePrerequisiteChecker({ data });
  const canDisplaySidePanel = checkIfSidePanelCanBeDisplayed({ type: data?.type });
  const show = !!data && canDisplaySidePanel;

  const containsManyData = (detailsDataStack ?? []).length > 0;

  if (containsManyData) {
    const stackedSidePanels = (detailsDataStack ?? []).slice(1, detailsDataStack.length);
    const firstPanelData = detailsDataStack[0];
    const isSinglePanel = stackedSidePanels.length === 0;

    return (
      <>
        {/* TODO: [PROF-4973] Refactor rendering side panels when animations will be fixed */}
        <BookingDetailSidePanel
          isOnTop={isSinglePanel}
          show={show && firstPanelData?.type === "booking"}
          data={firstPanelData}
          onDeleted={onDeleted}
          onEdited={onEdited}
          onClose={closeDetailsPanel}
        />
        <EngagementSidePanelWrapper
          isOnTop={isSinglePanel}
          show={show && firstPanelData?.type === "engagement"}
          data={firstPanelData}
          onClose={closeDetailsPanel}
        />
        <ProfileDetailsSidePanel
          isOnTop={isSinglePanel}
          show={show && firstPanelData?.type === "profile"}
          data={firstPanelData}
          onClose={closeDetailsPanel}
        />
        <RoleDetailSidePanelWrapper
          isOnTop={isSinglePanel}
          show={show && firstPanelData?.type === "role"}
          data={firstPanelData}
          onClose={closeDetailsPanel}
        />
        <OverbookingsDetailSidePanel
          isOnTop={isSinglePanel}
          show={show && firstPanelData?.type === "overbookings"}
          data={firstPanelData}
          onEdited={onEdited}
          onDeleted={onDeleted}
          onClose={closeDetailsPanel}
        />
        {stackedSidePanels.map((detailsData, index) => {
          const sidePanelType = detailsData.type;
          const Panel = SidePanel[sidePanelType];
          const isLastSidePanel = index === stackedSidePanels.length - 1;
          return (
            <Panel
              isOnTop={isLastSidePanel && !checkTopValue("booking_form")}
              key={`side-panel-${sidePanelType}-${detailsData.id}`}
              show
              data={detailsData}
              onDeleted={onDeleted}
              onEdited={onEdited}
              onClose={closeDetailsPanel}
            />
          );
        })}
      </>
    );
  }
  const isOnTop = !checkTopValue("booking_form");
  // TODO: [PROF-4973] Refactor rendering side panels when animations will be fixed
  return (
    <>
      <BookingDetailSidePanel
        isOnTop={isOnTop}
        show={show && data?.type === "booking"}
        data={data}
        onDeleted={onDeleted}
        onEdited={onEdited}
        onClose={closeDetailsPanel}
      />
      <EngagementSidePanelWrapper
        isOnTop={isOnTop}
        show={show && data?.type === "engagement"}
        data={data}
        onClose={closeDetailsPanel}
      />
      <ProfileDetailsSidePanel
        isOnTop={isOnTop}
        show={show && data?.type === "profile"}
        data={data}
        onClose={closeDetailsPanel}
      />
      <RoleDetailSidePanelWrapper
        isOnTop={isOnTop}
        show={show && data?.type === "role"}
        data={data}
        onClose={closeDetailsPanel}
      />
      <OverbookingsDetailSidePanel
        isOnTop={isOnTop}
        show={show && data?.type === "overbookings"}
        data={data}
        onEdited={onEdited}
        onDeleted={onDeleted}
        onClose={closeDetailsPanel}
      />
    </>
  );
};
